/**
 * Module 73 (WP-73.7, user decision D4) — when may a power-up button glow to
 * say "you can try this free, now"?
 *
 * The user's constraint is the whole design: **the player must never get an
 * annoying message or notification during play.** So this is a button-level
 * glow only — never a toast, popup, banner, sound or coach dim — and it shows
 * only when EVERY rule below holds. Pure, so each rule is testable on its own;
 * the plugin supplies the facts and remembers what was shown.
 *
 * 1. The power-up has free uses left and has never been used on this track
 *    (free uses left === the full grant — a paid use only happens after the
 *    grant is spent, so "grant intact" IS "never used").
 * 2. It can act on this board and nothing else refuses it (`refusal === null`:
 *    not locked, not capped, not Module 70's `nothingToDo`).
 * 3. The player looks stuck on this puzzle: at least 2 undos, a restart, or
 *    70% of the move budget used.
 * 4. Nothing else is asking for attention (tutorial, coach, popup, armed
 *    power-up, the Daily) — the caller folds that into `busy`.
 * 5. At most 3 puzzles per power-up per track (once per puzzle is the caller's
 *    job: it asks this once and remembers the answer for the puzzle).
 *
 * At most ONE button is picked, the first eligible in bar order: several
 * glowing at once would be exactly the noise rule 4 exists to prevent.
 */
export const POWERUP_HINT = {
    minUndos: 2,
    budgetFraction: 0.7,
    maxPuzzlesPerTrack: 3,
};
/** Rule 3. */
export function looksStuck(m) {
    if (m.undos >= POWERUP_HINT.minUndos || m.restarted)
        return true;
    return m.budget !== null && m.budget > 0 && m.movesUsed >= m.budget * POWERUP_HINT.budgetFraction;
}
/** Rules 1–5 for one power-up. `shownOnTrack` = puzzles it has already glowed on. */
export function hintEligible(p, m, shownOnTrack) {
    return (p.unlocked &&
        p.freeUses > 0 &&
        p.freeUsesLeft === p.freeUses &&
        p.refusal === null &&
        !m.busy &&
        looksStuck(m) &&
        shownOnTrack < POWERUP_HINT.maxPuzzlesPerTrack);
}
/** The one power-up to glow now, first eligible in bar order, or null. */
export function pickHint(powerups, m, shown) {
    return powerups.find((p) => hintEligible(p, m, shown[p.id] ?? 0))?.id ?? null;
}
